import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { UseFormGetValues, UseFormSetError } from 'react-hook-form';

import ErrorParser, { ErrorKeys } from '../../utils/parsers/error-parser.ts';
import { FieldsErrorsType } from '../../constants/types/axios.types.ts';
import { useSnackbarStack } from '../../providers/SnackbarProvider.tsx';
import { EXPIRED_TOKEN_MESSAGE_KEY } from '../../constants/config.ts';

interface IErrorHandlerOptions {
  setError: UseFormSetError<any>;
  getValues: UseFormGetValues<any>;
  keysToSnackbar?: string[];
}

const useErrorHandler = (form: IErrorHandlerOptions | void, ignoreErrors?: string[], disableSnackbars = false) => {
  const { t } = useTranslation('shared/validation');
  const { pushSnackbar } = useSnackbarStack();

  const handlePushSnackbar = (messageKey: string) => {
    if (!disableSnackbars && !ignoreErrors?.includes(messageKey) && messageKey !== EXPIRED_TOKEN_MESSAGE_KEY) {
      pushSnackbar({ text: t(messageKey) });
    }
  };

  return (error: AxiosError<FieldsErrorsType>) => {
    const parsedError = ErrorParser.parseError(error, !form);

    if (parsedError) {
      const { key, errors } = parsedError;

      switch (key) {
        case ErrorKeys.FormValidation: {
          const { setError, getValues, keysToSnackbar } = form || {};
          const formKeys = Object.keys(getValues?.() || {});

          errors?.forEach(({ property, messageKey }) => {
            if (
              property &&
              formKeys.includes(property.split('.')[0]) &&
              !keysToSnackbar?.includes(property)
            ) {
              return setError?.(property as string, { message: { key: messageKey } as any });
            }

            return handlePushSnackbar(messageKey);
          });

          return;
        }

        case ErrorKeys.ErrorsToSnackbar: {
          errors?.map(({ messageKey }) => handlePushSnackbar(messageKey));
          return;
        }

        case ErrorKeys.Network: {
          handlePushSnackbar('network_error');
          return;
        }

        default:
          handlePushSnackbar('unknown_error');
      }
    }
  };
};

export default useErrorHandler;
